import {isSystemMetric} from '../../../../util/plotHelpers/prettifyMetricName';
import {type RunSetQuery} from '../../../../util/queryTypes';
import {type DeltaQueryTypeOverride} from '../../types';
import {type BucketedQueryType} from './../queries/queryVars';

/*
This function selects one of six active query types based on runtime conditions:

1. **ID Type** (Id suffix):
   - Standard queries use projectId/entityId from the current workspace/report context
   - "Id" suffix queries use internalProjectId for cross-project references (e.g., in reports with multiple runsets)

2. **Grouping** (Grouped prefix):
   - Standard queries fetch individual runs
   - "Grouped" queries leverage backend aggregation when enabled, reducing frontend processing load
   - Grouped queries are only used when: backend grouping is enabled, querying a single runset,
     displaying grouped data, not aggregating metrics (aggregateMetrics is a client-only
     reduction — see WB-36605), and not querying system metrics

3. **Line Construction** (BackendLines infix):
   - Standard queries use frontend line construction
   - "BackendLines" queries use backend-constructed line data for improved performance
   - Backend line construction requires BOTH usesBackendLineConstruction AND allowsBackendLineConstruction
   - Grouped backend line construction additionally requires allowsGroupedBackendLineConstruction
   - Backend line construction is NOT enabled with internal ID queries (implementation exists but not wired up)

Active Query Types (6):
- BucketedQuery (baseline)
- BucketedQueryId (baseline + internal ID)
- GroupedBucketedQuery (baseline + backend grouping)
- GroupedBucketedQueryId (baseline + backend grouping + internal ID)
- BucketedBackendLinesQuery (baseline + backend line construction, standard ID only)
- GroupedBucketedBackendLinesQuery (backend grouping + backend line construction, standard ID only)

Columnar Experiment (BucketedColumnarQuery):
- When allowsColumnarBucketed is on, standard queries fetch from the Connect
  columnar endpoint instead of GraphQL. Skipped for internal-ID, grouped,
  pinned/baseline-run, and metric-aggregation panels.

Not Enabled:
- BucketedBackendLinesQueryId - Implementation exists but is not wired up in getQueryType()

Admin Override (deltaQueryTypeOverride):
- W&B admins can force the line-construction dimension from the panel editor.
  'backendLines' forces a BackendLines variant, but only when the panel
  structurally supports backend line construction (supportsBackendLineConstruction)
  — this lets an admin bypass the ramp-flag rollout for testing without forcing
  backend lines on a panel that would render incorrect data; it is disabled
  otherwise. 'bucketed' forces frontend line construction. Only the
  line-construction dimension is overridden — the grouping and internal-ID
  dimensions are still resolved by the logic below, so the override never emits
  the unsupported BucketedBackendLinesQueryId.

See architecture.md for full documentation.
*/
export const getQueryType = ({
  aggregateMetrics,
  allowsBackendLineConstruction,
  allowsColumnarBucketed,
  allowsGroupedBackendLineConstruction,
  enableBackendGrouping,
  hasPinnedRuns,
  historyKeys,
  isGrouped,
  runSets,
  usesInternalId,
  deltaQueryTypeOverride,
  supportsBackendLineConstruction,
}: {
  aggregateMetrics?: boolean;
  allowsBackendLineConstruction: boolean;
  allowsColumnarBucketed: boolean;
  allowsGroupedBackendLineConstruction: boolean;
  enableBackendGrouping: boolean;
  hasPinnedRuns: boolean;
  historyKeys: string[];
  isGrouped: boolean;
  runSets: RunSetQuery[];
  usesInternalId: boolean;
  deltaQueryTypeOverride?: DeltaQueryTypeOverride;
  supportsBackendLineConstruction?: boolean;
}): BucketedQueryType => {
  // Admin override of the line-construction dimension. 'backendLines' forces it
  // ON, but only when the panel structurally supports backend line construction
  // — this lets an admin bypass the ramp-flag rollout for testing without
  // forcing backend lines on a panel that would render incorrect data (it is
  // disabled in that case). 'bucketed' forces frontend construction. With no
  // override, defer to the ramp-flag-derived capability inputs.
  const overrideForcesBackendLines =
    deltaQueryTypeOverride === 'backendLines' &&
    supportsBackendLineConstruction === true;
  const overrideForcesBucketed = deltaQueryTypeOverride === 'bucketed';

  const effectiveAllowsBackendLineConstruction = overrideForcesBackendLines
    ? true
    : overrideForcesBucketed
      ? false
      : allowsBackendLineConstruction;
  const effectiveAllowsGroupedBackendLineConstruction =
    overrideForcesBackendLines
      ? true
      : overrideForcesBucketed
        ? false
        : allowsGroupedBackendLineConstruction;

  // Metric aggregation (config.aggregateMetrics) is a client-only reduction:
  // the backend groups *runs* but has no argument to collapse across metrics,
  // and useLines skips its client metric-collapse step once a runset comes back
  // backend-aggregated (GroupedRunConnection). So when aggregateMetrics is on we
  // must decline backend grouping and fetch ungrouped runs, letting useLines
  // group runs *and* collapse metrics on the client. See WB-36605.
  const useGroupedQuery =
    enableBackendGrouping &&
    runSets.length < 2 &&
    isGrouped &&
    !aggregateMetrics &&
    !historyKeys.some(key => isSystemMetric(key));

  if (useGroupedQuery) {
    if (effectiveAllowsGroupedBackendLineConstruction && !usesInternalId) {
      return 'GroupedBucketedBackendLinesQuery';
    }
    return usesInternalId ? 'GroupedBucketedQueryId' : 'GroupedBucketedQuery';
  }

  if (effectiveAllowsBackendLineConstruction && !usesInternalId) {
    return 'BucketedBackendLinesQuery';
  }

  if (
    allowsColumnarBucketed &&
    !usesInternalId &&
    !isGrouped &&
    !hasPinnedRuns &&
    !aggregateMetrics
  ) {
    return 'BucketedColumnarQuery';
  }

  return usesInternalId ? 'BucketedQueryId' : 'BucketedQuery';
};
